import axios from 'axios';

// Configure base API URL from environment variable
// Development: http://localhost:18080/api
// Production: https://factory.servicetag.services.signify.com
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:18080/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
  withCredentials: true, // Include cookies for authentication
});

// Request interceptor for adding auth tokens if needed
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('authToken');
      // Redirect to login or show auth modal
    }
    return Promise.reject(error);
  }
);

export const bomAPI = {
  uploadPackageFile: async (endpointSuffix, fileBlob, fileName) => {
    try {
      const formData = new FormData();
      formData.append('file', fileBlob, fileName);

      const response = await axios.post(`${API_BASE_URL}/standard-product/links/bom/${endpointSuffix}`, formData, {
        withCredentials: true,
        timeout: 30000,
        headers: {
          Accept: 'application/json, text/plain, */*',
        },
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || `Failed to upload ${endpointSuffix} file`);
    }
  },

  uploadPackageFiles: async (endpointSuffix, files) => {
    try {
      const normalizedFiles = Array.isArray(files) ? files : [];
      if (normalizedFiles.length === 0) {
        throw new Error('No files provided for upload');
      }

      const formData = new FormData();
      normalizedFiles.forEach((file) => {
        formData.append('file', file, file.name);
      });

      const response = await axios.post(`${API_BASE_URL}/standard-product/links/bom/${endpointSuffix}`, formData, {
        withCredentials: true,
        timeout: 30000,
        headers: {
          Accept: 'application/json, text/plain, */*',
        },
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || `Failed to upload ${endpointSuffix} files`);
    }
  },

  uploadFilesToPath: async (path, files) => {
    try {
      const normalizedFiles = Array.isArray(files) ? files : [];
      if (normalizedFiles.length === 0) {
        throw new Error('No files provided for upload');
      }

      const formData = new FormData();
      normalizedFiles.forEach((file) => {
        formData.append('file', file, file.name);
      });

      const response = await axios.post(`${API_BASE_URL}${path}`, formData, {
        withCredentials: true,
        timeout: 30000,
        headers: {
          Accept: 'application/json, text/plain, */*',
        },
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || `Failed to upload files to ${path}`);
    }
  },

  // Upload BOM data to backend (production endpoint)
  uploadBOM: async (bomData) => {
    try {
      const response = await apiClient.post('/standard-product/links/bom', {
        bomData,
        timestamp: new Date().toISOString(),
        totalBatches: Object.keys(bomData).length,
        totalItems: Object.values(bomData).reduce((sum, batch) => sum + batch.itemCount, 0)
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload BOM data');
    }
  },

  // Upload BOM CSV file as multipart/form-data (file part)
  uploadBOMFile: async (fileBlob, fileName = 'file.csv', factoryCode = '') => {
    try {
      const formData = new FormData();
      formData.append('file', fileBlob, fileName);

      const endpoint = factoryCode
        ? `${API_BASE_URL}/standard-product/links/bom/${encodeURIComponent(factoryCode)}`
        : `${API_BASE_URL}/standard-product/links/bom`;

      const response = await axios.post(endpoint, formData, {
        withCredentials: true,
        timeout: 30000,
        headers: {
          Accept: 'application/json, text/plain, */*',
        },
      });

      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload BOM file');
    }
  },

  uploadCSVFile: async (fileBlob, fileName = 'file.csv') => {
    return bomAPI.uploadPackageFile('csv', fileBlob, fileName);
  },

  uploadCSVFiles: async (files) => {
    return bomAPI.uploadFilesToPath('/standard-product/links/csv', files);
  },

  uploadBOMPackageFile: async (fileBlob, fileName = 'file.json') => {
    return bomAPI.uploadPackageFile('bom', fileBlob, fileName);
  },

  uploadBOMPackageFiles: async (files) => {
    return bomAPI.uploadPackageFiles('bom', files);
  },

  uploadBOMPackageFilesByFactory: async (factoryCode, files) => {
    const safeFactoryCode = encodeURIComponent(String(factoryCode || '').trim());
    return bomAPI.uploadFilesToPath(`/standard-product/links/bom/${safeFactoryCode}`, files);
  },

  getBOMLinks: async () => {
    try {
      const response = await apiClient.get('/standard-product/links/bom');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch BOM links');
    }
  },

  // Get BOM status
  getBOMStatus: async (bomId) => {
    try {
      const response = await apiClient.get(`/bom/${bomId}/status`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get BOM status');
    }
  },

  // Get all BOM records
  getAllBOMs: async () => {
    try {
      const response = await apiClient.get('/bom');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch BOM records');
    }
  },

  // Delete BOM record
  deleteBOM: async (bomId) => {
    try {
      const response = await apiClient.delete(`/bom/${bomId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete BOM record');
    }
  },

  // Validate service tags
  validateServiceTags: async (serviceTags) => {
    try {
      const response = await apiClient.post('/bom/validate', { serviceTags });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to validate service tags');
    }
  }
};

export default apiClient;